import { z } from "zod";

export const WorkflowGuardrailAssignmentConfigSchema = z.object({
  guardrailDefinitionId: z.string().uuid(),
  sortOrder: z.number().int().min(0),
  enabled: z.boolean().default(true),
});

export type WorkflowGuardrailAssignmentConfig = z.infer<
  typeof WorkflowGuardrailAssignmentConfigSchema
>;

export const WorkflowGuardrailAssignmentConfigListSchema = z.array(
  WorkflowGuardrailAssignmentConfigSchema,
);

function compareAssignments(
  left: WorkflowGuardrailAssignmentConfig,
  right: WorkflowGuardrailAssignmentConfig,
): number {
  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder;
  }
  return left.guardrailDefinitionId.localeCompare(right.guardrailDefinitionId);
}

export function normalizeWorkflowGuardrailAssignmentConfigs(
  assignments: readonly WorkflowGuardrailAssignmentConfig[],
): WorkflowGuardrailAssignmentConfig[] {
  const deduped = new Map<string, WorkflowGuardrailAssignmentConfig>();

  for (const assignment of [...assignments].sort(compareAssignments)) {
    if (deduped.has(assignment.guardrailDefinitionId)) {
      continue;
    }
    deduped.set(assignment.guardrailDefinitionId, {
      guardrailDefinitionId: assignment.guardrailDefinitionId,
      sortOrder: assignment.sortOrder,
      enabled: assignment.enabled,
    });
  }

  return Array.from(deduped.values()).map((assignment, index) => ({
    guardrailDefinitionId: assignment.guardrailDefinitionId,
    sortOrder: index,
    enabled: assignment.enabled,
  }));
}

export function getWorkflowGuardrailAssignmentConfigs(
  config: Record<string, unknown> | null | undefined,
): WorkflowGuardrailAssignmentConfig[] {
  const parsed = WorkflowGuardrailAssignmentConfigListSchema.safeParse(
    config?.guardrailAssignments,
  );
  if (!parsed.success) {
    return [];
  }
  return normalizeWorkflowGuardrailAssignmentConfigs(parsed.data);
}

export function withWorkflowGuardrailAssignmentConfigs(
  config: Record<string, unknown> | null | undefined,
  assignments: readonly WorkflowGuardrailAssignmentConfig[],
): Record<string, unknown> {
  const normalizedAssignments =
    normalizeWorkflowGuardrailAssignmentConfigs(assignments);
  const nextConfig = { ...(config ?? {}) };

  if (normalizedAssignments.length === 0) {
    delete nextConfig.guardrailAssignments;
    return nextConfig;
  }

  return {
    ...nextConfig,
    guardrailAssignments: normalizedAssignments,
  };
}
